import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import pluralize from 'pluralize';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import PersonIcon from '@material-ui/icons/Person';
import AppActions from '../../actions/app-actions';
// import AppStore from '../../stores/app-store';
import { useTableState, useTimeout } from '../../custom-hooks';
import CTable from '../common/ctable';
import { showError } from '../../helpers';
import Loader from '../common/loader';
import { columns } from './device-table-def';
import DeviceDialog from './device-dialog';

const timeoutInMilliSec = 30 * 1000;
const columnDefinition = columns();

const DeviceManagement = () => {
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState({ open: false });
  const [tenantList, setTenantList] = useState([]);
  const { colDef, selectedRows, onRowSelect, onAllRowSelect, onSelectedRowsReset, onFilterChange, onColumnChange } = useTableState(columnDefinition);
  const [startTimer] = useTimeout(fetchDetails, timeoutInMilliSec);

  const leftSideToolbarActions = useCallback(() => {
    if (selectedRows.length) {
      return (
        <>
          <Tooltip title='Delete'>
            <IconButton color='primary' onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Unlink'>
            <IconButton color='primary' onClick={onUnlink}>
              <LinkOffIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Change Tenant'>
            <IconButton color='primary' onClick={OnTenantChange}>
              <PersonIcon />
            </IconButton>
          </Tooltip>
          {
            selectedRows.length === 1
              ? (
                <Tooltip title='Change Key'>
                  <IconButton color='primary' onClick={onPublikeyChange}>
                    <VpnKeyIcon />
                  </IconButton>
                </Tooltip>
              )
              : null
          }
        </>
      );
    } else {
      return null;
    }
  }, [selectedRows, list]);

  const onPublicKeyInfo = useCallback(data => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    setDlg(() => ({ open: true, type: 'publickey', handleCancel, ...data }));
  }, []);

  function onDelete () {
    const selCount = selectedRows.length;
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      const delPromises = selectedRows.map(sr => AppActions.deleteTenantDeviceMap(sr.id));
      Promise.all(delPromises)
        .then(() => {
          const msg = selCount > 1 ? `Selected devices are deleted`: `Selected device is deleted`;
          const newList = list.filter(fId => (selectedRows.findIndex(srId => fId.id === srId.id)) === -1);
          ReactDOM.unstable_batchedUpdates(() => {
            setList(() => newList);
            setDlg(() => ({ open: false }));
            onSelectedRowsReset();
            AppActions.setSnackbar(msg);
          });
        })
        .catch(err => {
          showError(err, `Can't delete the selected ${pluralize('device', selCount)}`);
        })
    };
    setDlg(() => ({ open: true, type: 'delete', handleOk, handleCancel, selCount, tenantList })); 
  }

  function onUnlink () {
    const selCount = selectedRows.length;
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      const unlinkPromises = selectedRows.map(sr => AppActions.editTenantDeviceMap(sr.id, { Tenant: '' }));
      Promise.all(unlinkPromises)
        .then(() => {
          const msg = selCount > 1 ? 'Selected devices are unlinked from tenant': 'Selected device is unlinked from tenant';
          const newList = list.map(item => {
            const fd = selectedRows.find(sr => item.id === sr.id);
            if (fd) {
              return { ...item, Tenant: '' };
            } else {
              return item;
            }
          });
          ReactDOM.unstable_batchedUpdates(() => {
            setList(() => newList);
            setDlg(() => ({ open: false }));
            onSelectedRowsReset();
            AppActions.setSnackbar(msg);
          });
        })
        .catch(err => {
          const newList = list.map(item => {
            const fd = selectedRows.find(sr => item.id === sr.id);
            if (fd) {
              return { ...item, Tenant: '' };
            } else {
              return item;
            }
          });
          ReactDOM.unstable_batchedUpdates(() => {
            setList(() => newList);
            setDlg(() => ({ open: false }));
            onSelectedRowsReset();
            showError(err, `Can't/Partially unlink the selected ${pluralize('device', selCount)}`);
          });
        })
    };
    setDlg(() => ({ open: true, type: 'unlink', handleOk, handleCancel, selCount }));
  }

  function onPublikeyChange () {
    const selRow = selectedRows[0];
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (data) => {
      AppActions.editTenantDeviceMap(selRow.id, data)
        .then(() => {
          const newList = list.map(item => {
            const fd = selectedRows.find(sr => item.id === sr.id);
            if (fd) {
              return { ...item, ...data };
            } else {
              return item;
            }
          });
          ReactDOM.unstable_batchedUpdates(() => {
            setList(() => newList);
            setDlg(() => ({ open: false }));
            onSelectedRowsReset();
            AppActions.setSnackbar('Successfully changed the public key');
          });
        })
        .catch(err => {
          showError(err, `Can't change the public key`);
        })
    };
    setDlg(() => ({ open: true, type: 'setpublickey', handleOk, handleCancel, ...selRow }));    
  }

  function OnTenantChange () {
    const selCount = selectedRows.length;
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (data) => {
      const tenantPromises = selectedRows.map(sr => AppActions.editTenantDeviceMap(sr.id, data));
      Promise.all(tenantPromises)
        .then(() => {
          const msg = selCount > 1 ? `Selected devices are assigned to tenant "${data.Tenant}"`: `Selected device is assigned to tenant "${data.Tenant}"`;
          const newList = list.map(item => {
            const fd = selectedRows.find(sr => item.id === sr.id);
            if (fd) {
              return { ...item, ...data };
            } else {
              return item;
            }
          });
          ReactDOM.unstable_batchedUpdates(() => {
            setList(() => newList);
            setDlg(() => ({ open: false }));
            onSelectedRowsReset();
            AppActions.setSnackbar(msg);
          });
        })
        .catch(err => {
          showError(err, `Can't assing the selected ${pluralize('device', selCount)} to the tenant "${data.Tenant}"`);
        })
    };
    setDlg(() => ({ open: true, type: 'edit', handleOk, handleCancel, selCount, tenantList }));    
  }

  function onCreateDeviceMap () {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (UUID, Tenant, publicKey) => {
      AppActions.createTenantDeviceMap({ UUID, Tenant, public_key: publicKey })
        .then(res => {
          console.log(res);
          ReactDOM.unstable_batchedUpdates(() => {
            setList([...list, res.body]);
            setDlg(() => ({ open: false }));
            AppActions.setSnackbar(`A new Tenant-Device map "${UUID}" created successfully.`);
          });
        })
        .catch((err) => {
          ReactDOM.unstable_batchedUpdates(() => {
            showError(err, `Tenant-Device map create failed.`);
            setDlg(() => ({ open: false }));
          });
        });
    };
    setDlg(() => ({ open: true, type: 'create', handleCancel, handleOk, tenantList }));
  }

  function onFileLoad (e) {
    const files = e.target.files || []; 
    const file = files[0] || {};
    if (file.type === 'application/json') {
      const fileReader = new FileReader();

      fileReader.onload = (successEvent) => {
        const result = successEvent.target.result || '';
        const data = result.split(',');
        if (Array.isArray(data) && data.length > 1) {
          setLoading(true);
          AppActions.uploadTenantDeviceMap({devmap: data[1]})
            .then(() => {
              ReactDOM.unstable_batchedUpdates(() => {
                fetchDetails();
                AppActions.setSnackbar('Json file was successfully uploaded.');
              });
            })
            .catch(err => {
              ReactDOM.unstable_batchedUpdates(() => {
                showError(err, `Failed to upload json file.`);
                setLoading(false);
              });
            });
        }
      };

      fileReader.onerror = (errorEvent) => {
        AppActions.setSnackbar(`File could not be read! Code: ${errorEvent.target.error.code}`);
      };

      fileReader.readAsDataURL(file);
    } else {
      AppActions.setSnackbar('Invalid file type. Please upload json file.');
    }
  }

  const loader = () => {
    return (
      <div>
        <Loader show={true} />
      </div>        
    );
  };

  const bodyContent = () => {
    if (loading) {
      return loader();
    } else {
      return (
      <>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Grid container justify='flex-end' spacing={1}>
              <Grid item>
                <Button variant='contained' color='primary' onClick={onCreateDeviceMap}>Create New Tenant-Device Map</Button>
              </Grid>
              <Grid item>
                <input accept='.json' id='contained-button-file' multiple type='file' style={{ display: 'none' }} onChange={onFileLoad}/>
                <label htmlFor='contained-button-file'>
                  <Button variant='contained' color='primary' component='span'>Upload Json File</Button>
                </label>
              </Grid>       
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <CTable 
              colDef={colDef}
              data={list}
              selectedRows={selectedRows}
              noOfNonSelectableRows={0}
              leftSideToolbarActions={leftSideToolbarActions}
              onRowSelect={onRowSelect}
              onFilterChange={onFilterChange}
              onColumnChange={onColumnChange}
              onAllRowSelect={onAllRowSelect}
              onPublicKeyInfo={onPublicKeyInfo}
            />
          </Grid>
        </Grid>
        {dlg.open ? <DeviceDialog {...dlg} /> : null}
      </>
      );
    }
  };

  function fetchDetails () {
    Promise.all([AppActions.getAllTenantDeviceMap(), AppActions.getAllTenants()])
      .then(responses => {
        ReactDOM.unstable_batchedUpdates(() => {
          setList(responses[0] || []);
          setTenantList(responses[1] || []);
          setLoading(false);
        });
      })
      .catch(err => {
        ReactDOM.unstable_batchedUpdates(() => {
          showError(err, `Tenants-Device map couldn't be loaded.`);
          setLoading(false);
        });
      })
      .finally(() => startTimer());
  }

  useEffect(() => {
    fetchDetails();
  }, []);

  return (
    <>
      {bodyContent()}
    </>
  );
};

export default DeviceManagement;
